import { useEffect } from 'react';

import { useAtomValue } from 'jotai';

import { useFlags } from '@/hooks/useFlags';

import { type ColorModePreference, userPrefsAtom } from '@/atoms/userPrefs';

/** DOM `data-color-mode` values (`system` preference maps to `auto`). */
type DataColorMode = 'auto' | 'light' | 'dark';

export const toDataColorMode = (
  preference: ColorModePreference,
): DataColorMode => (preference === 'system' ? 'auto' : preference);

// Browsers use <meta name="theme-color"> for status/address-bar chrome.
// CSS vars can't go in that attribute, so hexes are hard-coded here
// and kept in sync with data-color-mode (and OS preference when mode is auto).
const THEME_COLOR_META = 'theme-color';
const LIGHT_THEME_COLOR = '#ffffff';
const DARK_THEME_COLOR = '#1a1f23';

const resolveThemeColor = (mode: DataColorMode): string => {
  if (mode === 'dark') {
    return DARK_THEME_COLOR;
  }
  if (mode === 'light') {
    return LIGHT_THEME_COLOR;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? DARK_THEME_COLOR
    : LIGHT_THEME_COLOR;
};

const setThemeColorMeta = (color: string) => {
  let meta = document.querySelector(`meta[name="${THEME_COLOR_META}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', THEME_COLOR_META);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', color);
};

/**
 * Maps the user's theme preference to `data-color-mode` DOM attribute.
 *
 * Gated by LD `darkMode` flag. When the flag is off, attributes and
 * theme-color meta are cleared so no stale dark state remains.
 */
export const useColorMode = () => {
  const { theme } = useAtomValue(userPrefsAtom);
  const { darkMode: darkModeFF } = useFlags();

  useEffect(() => {
    if (!darkModeFF) {
      delete document.body.dataset.colorMode;
      delete document.documentElement.dataset.colorMode;
      setThemeColorMeta(LIGHT_THEME_COLOR);
      return;
    }

    const mode = toDataColorMode(theme);
    document.body.dataset.colorMode = mode;
    delete document.documentElement.dataset.colorMode;
    setThemeColorMeta(resolveThemeColor(mode));

    // CSS auto mode follows OS preference; keep theme-color meta in sync too.
    if (mode !== 'auto') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const onPreferenceChange = () => {
      setThemeColorMeta(resolveThemeColor('auto'));
    };
    mediaQuery.addEventListener('change', onPreferenceChange);
    return () => {
      mediaQuery.removeEventListener('change', onPreferenceChange);
    };
  }, [theme, darkModeFF]);
};
